import { MRT_ColumnDef } from 'material-react-table';
import CloseIcon from '@mui/icons-material/Close';
import Snackbar from 'src/presentations/components/atom/snackbar/Snackbar';
import { FC, useCallback, useMemo, useState } from 'react';
import { LegalEntityDto } from 'src/generated/services/TFinancialApi';
import {
  LegalEntityFilters,
  useLegalEntitiesMetadata,
  useLegalEntityList,
  useMigrateLegalEntity,
  useUpsertLegalEntity,
} from 'src/use-cases/settings/useLegalEntities.ts';
import { createFilterItems } from 'src/presentations/components/molecules/table/columnHeader';
import CopySuccessIcon from 'src/assets/images/icons/copy-success.svg';

import { useSettingsTable } from 'src/presentations/hooks/useSettingsTable';
import { LEGAL_ENTITY_FILTER_MAPPING, useTableFilters } from 'src/presentations/hooks/useTableFilters';
import { SettingsTableLayout } from 'src/presentations/components/templates/SettingsTableLayout';
import { SelectionIndicator } from './SelectionIndicator';
import MergeConfirmationModal from './modals/MergeConfirmationModal';
import { LegalEntityDetail } from './LegalEntityDetail';
import { SettingsColumnHeader } from '../SettingsColumnHeader';

// Extended type to track selection order
type LegalEntityWithSelection = LegalEntityDto & {
  selectionIndex?: number;
};

interface LegalEntitiesTableProps {
  searchQuery?: string;
}

export const LegalEntitiesMain: FC<LegalEntitiesTableProps> = (props) => {
  const { searchQuery } = props;

  // Selection state for merge functionality
  const [selectedRows, setSelectedRows] = useState<LegalEntityWithSelection[]>([]);
  const [rowSelection, setRowSelection] = useState<Record<string, boolean>>({});
  const [selectedMergeRows, setSelectedMergeRows] = useState<LegalEntityDto[]>([]);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isLoadingMerge, setIsLoadingMerge] = useState(false);
  const [isShowMergeSuccess, setIsShowMergeSuccess] = useState(false);
  const [selectionOrder, setSelectionOrder] = useState<number[]>([]);

  // Use the reusable settings table hook
  const {
    selectedEntityId,
    setSelectedEntityId,
    filters,
    entities,
    entitiesTotal,
    isLoading,
    isError,
    pagination,
    sorting,
    handleSortingChange,
    onChangePage,
    onChangePageSize,
    handleEntitySave,
    getTextFieldProps,
    clearValidationErrors,
    updateFilter,
    handleTableSort,
    metadataData,
    refetch,
    refetchMetadata,
  } = useSettingsTable<LegalEntityDto, LegalEntityFilters>({
    entityName: 'Legal entity',
    requiredFields: ['name', 'code', 'isBilledEntity'],
    useEntityList: useLegalEntityList,
    useEntityMetadata: useLegalEntitiesMetadata,
    useUpsertEntity: useUpsertLegalEntity,
    searchQuery,
    initialSorting: [{ id: 'id', desc: false }],
    // Custom request body preparation for legal entities
    prepareRequestBody: (filters, pagination, sorting) => {
      let sortingDirection: string | undefined;
      if (sorting.length > 0) {
        sortingDirection = sorting[0].desc ? 'DESC' : 'ASC';
      }

      return {
        page: pagination.pageIndex,
        size: pagination.pageSize,
        sortingField: sorting.length > 0 ? sorting[0].id : undefined,
        sortingDirection,
        filters,
      };
    },
    // Custom entity body preparation for legal entities
    prepareEntityBody: (values, original) => {
      let isBilledEntityValue = false;

      if (typeof values.isBilledEntity === 'string') {
        const stringValue = values.isBilledEntity.toLowerCase();
        isBilledEntityValue = stringValue === 'yes' || stringValue === 'true';
      } else if (typeof values.isBilledEntity === 'boolean') {
        isBilledEntityValue = values.isBilledEntity;
      }

      const codeValue = values.code?.toString() || '';

      return {
        ...(original?.id ? { id: original.id } : {}),
        name: values.name,
        code: codeValue,
        nationalCurrency: values.nationalCurrency,
        isBilledEntity: isBilledEntityValue,
        parentId: values.parentId,
        ...(original?.addresses ? { addresses: original.addresses } : {}),
      };
    },
  });

  // Use the reusable table filters hook
  const { handleTableFilter } = useTableFilters<LegalEntityFilters>({
    updateFilter,
    refetch,
    refetchMetadata,
  });

  // Legal entity specific filter handler
  const handleLegalEntityFilter = useCallback(
    (columnId: string, selectedValues: string[], allOptions?: { id: string; label: string; count: number }[]) => {
      // Handle special case for isBilledEntity filter
      if (columnId === 'isBilledEntity') {
        const mapping = LEGAL_ENTITY_FILTER_MAPPING[columnId];
        handleTableFilter(columnId, selectedValues, allOptions, {
          [columnId]: mapping,
        });
        return;
      }

      handleTableFilter(columnId, selectedValues, allOptions, LEGAL_ENTITY_FILTER_MAPPING);
    },
    [handleTableFilter]
  );

  const { mutate: migrateLegalEntity } = useMigrateLegalEntity();

  // Handle row selection changes
  const handleRowSelectionChange = useCallback(
    (updaterOrValue: Record<string, boolean> | ((old: Record<string, boolean>) => Record<string, boolean>)) => {
      const newSelection = typeof updaterOrValue === 'function' ? updaterOrValue(rowSelection) : updaterOrValue;

      // Track selection order by comparing old and new selections
      let newOrder = [...selectionOrder];

      // Find which rows were added or removed
      for (const [index, isSelected] of Object.entries(newSelection)) {
        const rowIndex = parseInt(index, 10);
        if (isSelected && !rowSelection[index]) {
          // Row was just selected - add to order
          newOrder.push(rowIndex);
        } else if (!isSelected && rowSelection[index]) {
          // Row was just deselected - remove from order
          newOrder = newOrder.filter((idx) => idx !== rowIndex);
        }
      }

      // Also check for rows that were in old selection but not in new selection
      for (const [index, isSelected] of Object.entries(rowSelection)) {
        const rowIndex = parseInt(index, 10);
        if (isSelected && !newSelection[index]) {
          // Row was deselected (removed from selection object)
          newOrder = newOrder.filter((idx) => idx !== rowIndex);
        }
      }

      setRowSelection(newSelection);
      setSelectionOrder(newOrder);

      // Convert row selection to selected entities in selection order
      const selectedEntities = newOrder.map((index) => entities[index]).filter((entity) => entity !== undefined);

      // Add selection index to track order
      const entitiesWithIndex = selectedEntities.map((entity, idx) => ({
        ...entity,
        selectionIndex: idx,
      }));

      setSelectedRows(entitiesWithIndex);
    },
    [rowSelection, entities, selectionOrder]
  );

  const handleMergeEntities = useCallback(() => {
    if (selectedRows.length !== 2) return;

    setIsLoadingMerge(true);

    migrateLegalEntity(
      {
        oldLegalEntityId: selectedRows[1].id as number,
        newLegalEntityId: selectedRows[0].id as number,
      },
      {
        onSuccess: () => {
          setIsLoadingMerge(false);
          setIsMergeModalOpen(false);
          setIsShowMergeSuccess(true);
          setSelectedMergeRows(selectedRows);
          setSelectedRows([]);
          setRowSelection({});
          setSelectionOrder([]);
        },
        onError: () => {
          setIsLoadingMerge(false);
        },
      }
    );
  }, [selectedRows, migrateLegalEntity]);

  const columns = useMemo(
    (): MRT_ColumnDef<LegalEntityDto>[] => [
      {
        enableEditing: false,
        accessorKey: 'id',
        header: 'ID',
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.names);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.names}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleLegalEntityFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('name'),
      },
      {
        accessorKey: 'code',
        header: 'Code',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.codes);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.codes}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleLegalEntityFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiTableHeadCellProps: {
          sx: { width: '250px' },
        },
        muiTableBodyCellProps: {
          sx: { width: '250px' },
        },
        Cell: ({ renderedCellValue }) => <span className='break-all'>{renderedCellValue?.toString() || ''}</span>,
        accessorFn: (row) => row.code?.toString() || '',
        muiEditTextFieldProps: {
          ...getTextFieldProps('code', { type: 'text' }),
        },
      },
      {
        accessorKey: 'nationalCurrency',
        header: 'National Currency',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.nationalCurrencies);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.nationalCurrencies}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleLegalEntityFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('nationalCurrency'),
      },
      {
        accessorKey: 'isBilledEntity',
        header: 'Billed Entity',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = [
            { id: 'Yes', label: 'Yes', count: metadataData?.data?.isBilledEntities?.true || 0 },
            { id: 'No', label: 'No', count: metadataData?.data?.isBilledEntities?.false || 0 },
            { id: 'null', label: '(Blanks)', count: metadataData?.data?.isBilledEntities?.null || 0 },
          ];
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.isBilledEntities?.map((value) => {
                if (value === true) return 'Yes';
                if (value === false) return 'No';
                return 'null';
              })}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleLegalEntityFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        accessorFn: (row) => {
          if (row.isBilledEntity === null) {
            return '-';
          }
          return row.isBilledEntity ? 'Yes' : 'No';
        },
        editVariant: 'select',
        editSelectOptions: ['Yes', 'No'],
        muiEditTextFieldProps: {
          ...getTextFieldProps('isBilledEntity'),
          select: true,
        },
      },
      {
        accessorKey: 'parentId',
        header: 'Parent Legal Entity',
        enableSorting: false,
        Header: ({ column }) => {
          const filterItems = createFilterItems(metadataData?.data?.parentIds);
          return (
            <SettingsColumnHeader
              column={column}
              filterItems={filterItems}
              currentFilters={filters.parentIds}
              onSort={handleTableSort}
              onFilter={(columnId, selectedValues) => handleLegalEntityFilter(columnId, selectedValues, filterItems)}
            />
          );
        },
        muiEditTextFieldProps: getTextFieldProps('parentId'),
      },
    ],
    [
      getTextFieldProps,
      handleTableSort,
      metadataData?.data,
      handleLegalEntityFilter,
      filters.names,
      filters.codes,
      filters.nationalCurrencies,
      filters.isBilledEntities,
      filters.parentIds,
    ]
  );

  // Custom cell props to handle row clicks for detail panel
  const customCellProps = ({ row, column }) => ({
    onClick: () => {
      if (column?.id !== 'mrt-row-actions') {
        setSelectedEntityId(row.original.id);
      }
    },
    sx: {
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'rgba(0, 0, 0, 0.04)',
        textDecoration: 'underline',
      },
    },
  });

  return (
    <>
      <SettingsTableLayout
        title='Legal Entities'
        subtitle='Configure the billed and legal entities'
        backPath='/settings'
        columns={columns}
        data={entities}
        totalItems={entitiesTotal}
        isLoading={isLoading}
        isError={isError}
        pagination={pagination}
        sorting={sorting}
        onChangePage={onChangePage}
        onChangePageSize={onChangePageSize}
        onSortingChange={handleSortingChange}
        onSaveEntity={handleEntitySave}
        onCancelEdit={clearValidationErrors}
        selectedEntityId={selectedEntityId}
        onCloseDetail={() => setSelectedEntityId(null)}
        renderDetail={(entityId) => <LegalEntityDetail legalEntityId={entityId as number} onClose={() => setSelectedEntityId(null)} />}
        customCellProps={customCellProps}
        enableRowSelection
        rowSelection={rowSelection}
        onRowSelectionChange={handleRowSelectionChange}
      />

      <SelectionIndicator
        selectedItems={selectedRows}
        onClearSelection={() => {
          setSelectedRows([]);
          setRowSelection({});
          setSelectionOrder([]);
        }}
        onMergeClick={() => setIsMergeModalOpen(true)}
      />

      {selectedRows.length === 2 && (
        <MergeConfirmationModal
          open={isMergeModalOpen}
          onClose={() => setIsMergeModalOpen(false)}
          onConfirm={handleMergeEntities}
          entityToDelete={selectedRows[1]}
          entityToKeep={selectedRows[0]}
          isLoading={isLoadingMerge}
        />
      )}

      {isShowMergeSuccess && selectedMergeRows.length === 2 && (
        <Snackbar
          className='p-16 rounded-12 bg-[#1C2429] gap-[66px] text-neutral-60'
          zIndex={50}
          autoSidebarPlacement
          position='bottom-left'
          icon={<img src={CopySuccessIcon} alt='' />}
          content={
            <p className='text-14'>
              <strong className='text-white font-bold'>{selectedMergeRows[1].code}</strong> has been merged into{' '}
              <strong className='text-white font-bold'>{selectedMergeRows[0].code}</strong>
            </p>
          }
          actions={
            <button type='button' onClick={() => setIsShowMergeSuccess(false)} aria-label='Close' className='text-neutral-50'>
              <CloseIcon />
            </button>
          }
        />
      )}
    </>
  );
};
